import React, { useContext, useRef } from 'react';
import Grid from '@mui/material/Grid';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor } from 'ckeditor5';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { FormControl } from '@mui/material';
import { calculateAgeOnDate } from 'src/util/util';
// `new Date(formatDate(x))` re-parsed a 'YYYY-MM-DD' string as UTC MIDNIGHT, so
// both pickers below showed the previous day at any negative UTC offset — the
// same idiom as TGCLOUD-1708, found by that ticket's sibling scan.
//
// `assignedDate` and `dateApproved` are `type: Date` (models/clinical-notes/
// contact-model.js) and the onChange below stores the picker's raw local Date, so
// they are INSTANT columns: their calendar day is read with local components.
// Plain `toLocalDate` would read the "…Z" string by its UTC components and move
// the day back at every POSITIVE offset instead — trading one offset's bug for
// the other's.
import {
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';
import {
  SnackContext,
  ThemeContext,
  AppContext,
  UserContext
} from 'src/store/ContextStore';
import TextInput from 'src/components/UI/input/TextInput';
import AssociatedNote from 'src/components/UI/input/AssociatedNote';
import {
  getReferralTags,
  getDependencies,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import { offeredStatusLabels } from 'src/util/statusSemantics';

import UploadFileList from 'src/components/UI/input/UploadFileList';
import { useAttachmentChanges } from 'src/components/UI/input/attachments';
import SelectInput from 'src/components/UI/input/SelectInput';
import {
  ExportToPdf,
  ExportToDocs,
  MergeFieldDropdown,
  TemplateDropdown,
  MergeFieldsPreviewDropdown,
  InsertConditionalBlock,
  InsertRepeatingBlock
} from 'src/plugins/ckeditor/CKEditorSetup.js';
import {
  baseConfig,
  basicPlugins,
  toolbar
} from 'src/components/settings/templates/constants.js';
import {
  exportToPDF,
  exportToDocs
} from 'src/components/settings/templates/util.js';
import 'ckeditor5/ckeditor5.css';
import { mergeFieldsOptions } from 'src/components/settings/templates/constants';
import { fetchMergeFieldCatalogue } from 'src/components/settings/templates/engine/catalogueClient';
import {
  buildCataloguePreviewDefaults,
  buildFullResolveContext,
  pickPrimaryRecord
} from 'src/components/settings/templates/engine/buildPreviewDefaults';
import { buildIndex } from 'src/components/settings/templates/engine/buildIndex';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import AnonymousFreeTextBanner from '../../../../AnonymousFreeTextBanner';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const getTitle = (gender) => {
  if (gender?.toLowerCase() === 'male') return 'Mr.';
  if (gender?.toLowerCase() === 'female') return 'Mrs.';
  return 'Unknown';
};

const getMergeFieldsDefaults = (mergeFieldsOptions, data, user, members) => {
  return mergeFieldsOptions.reduce((acc, field) => {
    if (field.id === 'title') {
      acc[field.id] = getTitle(data?.gender) || `{{${field.label}}}`;
    } else if (field.id === 'familyId') {
      acc[field.id] = user[field.id] || `{{${field.label}}}`;
    } else if (field.id === 'staffMember') {
      acc[field.id] =
        members?.length > 0 ? members.join(', ') : `{{${field.label}}}`;
    } else {
      acc[field.id] = (data && data[field.id]) || `{{${field.label}}}`;
    }
    return acc;
  }, {});
};

const ambiguousItemLabel = (item, meta) => {
  const name = item[meta.primaryField] || '(unnamed)';
  for (const f of meta.dateFields) {
    if (item[f]) {
      const d = new Date(item[f]);
      const dateText = Number.isNaN(d.getTime())
        ? item[f]
        : d.toLocaleDateString();
      return `${name} — ${dateText}`;
    }
  }
  return name;
};

export default function ContactForm({
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  data,
  memberTests,
  memberRecordsByType,
  isDisabled,
  setUnsavedChanges
}) {
  const t = useAutoTranslation();

  const editorRef = useRef(null);
  const patientDob = data?.dob;
  // The age on the calendar day of the contact date (calculateAgeOnDate). The
  // date is handed over as stored — a picker instant, or UTC midnight from the
  // spreadsheet importers — and only the raw value says which, so it is read by
  // toLocalDateFromPickerOrImport. `calculateAge` counted elapsed time from a
  // UTC-midnight birth date: a year short on the birthday at any positive
  // offset, and a positive age for a date before the birth, which is now blank.
  // The date field shows the day it read (asPickerDay), so the day on screen is
  // the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (contactDate) => calculateAgeOnDate(patientDob, contactDate),
    [patientDob]
  );

  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { user: loggedInUser } = React.useContext(UserContext);

  const defaultValues = isEditing
    ? { ...model }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        contactMode: '',
        contactType: '', // (Family History, Follow Up, etc. + Allow custom values)
        contactDate: new Date(),
        ageAtRecord: computeAgeAtRecord(new Date()),
        contactStatus: 'Scheduled', // Scheduled, Sent / Completed, Did Not Attend, Cancelled
        durationMinutes: 0, // Numeric field for duration in minutes
        staffMember: [loggedInUser._id], // Pre-populated with logged-in user
        fileAttachments: [], // Array to store file objects (e.g., { name, size, type, url })
        contactNotes: '',
        showInFamily: false,
        assignedDate: new Date(), // Renamed from "Date Requested for Checking"
        dateApproved: null, // Changed to `null` to indicate it's pending approval
        reminderEnabled: false,
        reminderDate: null
      };
  const { theme } = useContext(ThemeContext);
  let [referralOptions, setReferralOptions] = React.useState(['loading...']);
  const [formValues, setFormValues] = React.useState(defaultValues);
  // Attachment deletes wait for a successful save (TGCLOUD-1850).
  const attachmentChanges = useAttachmentChanges(defaultValues.fileAttachments);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);

  const { setSnack } = React.useContext(SnackContext);

  const allStaffMembers = React.useMemo(() => {
    return staffMemberOptions.map((options) => options.value);
  }, [staffMemberOptions]);

  const staffMembersMap = React.useMemo(() => {
    return staffMemberOptions.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);

  const members = React.useMemo(() => {
    return formValues.staffMember?.map((member) => staffMembersMap[member]);
  }, [formValues.staffMember, staffMembersMap]);

  const [catalogue, setCatalogue] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    fetchMergeFieldCatalogue().then((cat) => {
      if (mounted) setCatalogue(cat);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const mergeFieldsDefaults = React.useMemo(() => {
    const legacy = getMergeFieldsDefaults(
      mergeFieldsOptions,
      data,
      user,
      members
    );
    const cat = catalogue?.groups
      ? buildCataloguePreviewDefaults(catalogue.groups, {
          record: data || {},
          contactFormValues: formValues,
          staffNames: members || [],
          memberRecordsByType: memberRecordsByType || undefined,
          memberTests: memberTests || undefined
        })
      : {};
    return { ...legacy, ...cat };
  }, [
    data,
    user,
    members,
    catalogue,
    formValues,
    memberTests,
    memberRecordsByType
  ]);

  const mergeResolveContext = React.useMemo(
    () =>
      buildFullResolveContext({
        record: data || {},
        contactFormValues: formValues,
        staffNames: members || [],
        memberRecordsByType: memberRecordsByType || undefined,
        memberTests: memberTests || undefined
      }),
    [data, formValues, members, memberRecordsByType, memberTests]
  );

  const mergeCatalogueIndex = React.useMemo(
    () => (catalogue?.groups ? buildIndex(catalogue.groups) : null),
    [catalogue]
  );

  const [ambiguousRequest, setAmbiguousRequest] = React.useState(null);
  const resolveAmbiguity = React.useCallback(
    (ambiguous) =>
      new Promise((resolve) => setAmbiguousRequest({ ambiguous, resolve })),
    []
  );
  const handleAmbiguousConfirm = (choices) => {
    ambiguousRequest.resolve(choices);
    setAmbiguousRequest(null);
  };
  const handleAmbiguousCancel = () => {
    ambiguousRequest.resolve(null);
    setAmbiguousRequest(null);
  };

  const [ambiguousSelections, setAmbiguousSelections] = React.useState({});
  React.useEffect(() => {
    if (!ambiguousRequest) return;
    const defaults = {};
    for (const section of ambiguousRequest.ambiguous || []) {
      defaults[section.section] = pickPrimaryRecord(
        section.items,
        section.dateFields
      );
    }
    setAmbiguousSelections(defaults);
  }, [ambiguousRequest]);

  const [editorReady, setEditorReady] = React.useState(false);
  React.useEffect(() => {
    if (!editorReady || !editorRef.current) return;
    editorRef.current._lmResolveContext = mergeResolveContext;
    editorRef.current._lmCatalogueIndex = mergeCatalogueIndex;
  }, [editorReady, mergeResolveContext, mergeCatalogueIndex]);

  React.useEffect(() => {
    if (!editorReady || !editorRef.current) return;
    editorRef.current._lmGenerationMeta = {
      enabled: !!catalogue?.v2Enabled,
      familyId: user.familyId,
      familyMemberId
    };
  }, [editorReady, catalogue, user.familyId, familyMemberId]);

  // Feed the current Contact fields to the Insert Template dropdown so it can
  // surface a "Recommended" section (see recommendTemplates.js).
  React.useEffect(() => {
    if (!editorReady || !editorRef.current) return;
    editorRef.current._lmContactContext = {
      contactType: formValues.contactType,
      contactMode: formValues.contactMode,
      contactStatus: formValues.contactStatus
    };
  }, [
    editorReady,
    formValues.contactType,
    formValues.contactMode,
    formValues.contactStatus
  ]);

  const [reminderTouched, setReminderTouched] = React.useState(false);
  const computeDefaultReminderDate = React.useCallback((contactDate) => {
    if (!contactDate) return null;
    // default is the same day as contactDate
    return new Date(contactDate);
  }, []);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.contactDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.contactDate]);

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat,
      t
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: t('Associated record exists; check the {{value}}', {
          value: dep[0]
        }),
        severity: 'error'
      });
      setLoading(false);

      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //Make this a controller?
  React.useEffect(() => {
    const fetchData = async () => {
      const referralTags = await getReferralTags(
        theme,
        familyMemberId,
        formValues.familyId
      );
      setReferralOptions(referralTags);
    };
    if (referralOptions != [] && referralOptions[0] == 'loading...')
      fetchData();
  });

  // The values here come from the configured list now, like every other
  // picker. The three strings this replaces were never what the live picker
  // offered: it read an imported frozen array, and these were referenced only
  // from a second, commented-out picker further down the file.
  const [contactTypeOptions, setContactTypeOptions] = React.useState([]);
  const [contactStatusOptions, setContactStatusOptions] = React.useState([]);

  // TGCLOUD-1788 — was a hardcoded array with no setter, so a contact mode typed
  // into the picker was used for the record and then never offered again. The
  // four values above are the shipped seed for `contactModes` (declared in
  // backend/config/lookupDefinitions.js since TGCLOUD-1784), so nothing a site
  // already relies on disappears.
  const [contactModeOptions, setContactModeOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('contactModes').then(setContactModeOptions);
    getClinicalDefaults('contactTypes').then(setContactTypeOptions);
    getClinicalDefaults('contactStatuses').then(setContactStatusOptions);
  }, []);
  const handleChange = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));

    if (e.target.name === 'contactDate') {
      const nextContactDate = e.target.value;
      if (!formValues.reminderEnabled || !reminderTouched) {
        const nextDefault = computeDefaultReminderDate(nextContactDate);
        setFormValues((prev) => ({ ...prev, reminderDate: nextDefault }));
      }
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(nextContactDate)
      }));
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
  };

  const handleEditorChange = (event, editor) => {
    if (editor._lmPreviewActive) return;
    const data = editor.getData();
    // A blank canvas is no longer "this template" — drop stale provenance so an
    // ad-hoc letter isn't misattributed to a previously inserted template.
    if (!data || !data.trim()) editor._lmTemplateMeta = null;
    setFormValues((prev) => ({ ...prev, contactNotes: data }));
    if (data) setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  //Sure about this! Will be good for show in Family
  const onToggleSwitch = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
  };

  const [error, setError] = React.useState({});

  const validateForm = (values) => {
    let errors = {};

    if (values.contactType === '') {
      errors.contactType = t('Contact Type is required.');
    }

    if (!values.contactDate) {
      errors.contactDate = t('Contact Date is required.');
    }
    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    let requestChecking = null;
    let staffMember = null;

    if (formValues.requestChecking) {
      requestChecking = staffMembers.find(
        (member) => member.userId === formValues.requestChecking
      );
    }

    if (formValues.staffMember && formValues.staffMember.length > 0) {
      staffMember = formValues.staffMember
        .map((staff) => {
          let existStaffMember = staffMembers.find(
            (member) => member.userId === staff
          );

          return existStaffMember ? { ...existStaffMember } : null;
        })
        .filter(Boolean);
    }

    const parsePayload = {
      ...formValues,
      staffMember: staffMember ? [...staffMember] : [],
      requestChecking: requestChecking ? [requestChecking] : [],
      assignedDate:
        requestChecking && formValues.assignedDate
          ? formValues.assignedDate
          : null,
      dateApproved:
        formValues.approved && formValues.dateApproved
          ? formValues.dateApproved
          : null
    };

    handleClose({ exit: true });
    return axios
      .put(APICONSTANTS.contact, parsePayload)
      .then((res) => {
        if (res.status === 200) {
          attachmentChanges.commit(
            parsePayload.fileAttachments,
            familyMemberId
          );
          setSnack({
            status: true,
            msg: t('Successfully saved'),
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: t('Upload failed!'),
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Upload failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  const reqCheckingOptions = React.useMemo(() => {
    return staffMemberOptions.filter(
      (member) => !formValues.staffMember?.includes(member.value)
    );
  }, [staffMemberOptions, formValues]);

  return (
    //Should be fine to reuse old class?
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Contact ?"
        content={t(
          'Warning: This action will permanently delete this Contact. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box sx={{ position: 'relative' }}>
            <TextInput
              label="Contact Type"
              variant="outlined"
              fullWidth
              type={'autocomplete'}
              onChange={(e) => {
                const label = e ? e.label : '';
                handleChange({ target: { name: 'contactType', value: label } });
                // Persist the value as well as using it, so it is offered on
                // the next record. This picker has always shown privileged roles
                // an Add row; until now the value it produced was written to the
                // record and then lost, so the same clinician had to retype it
                // every time (TGCLOUD-1784).
                if (label) {
                  saveClinicalDefault('contactTypes', label).then(
                    setContactTypeOptions
                  );
                }
              }}
              onDelete={(option) => {
                deleteClinicalDefault('contactTypes', option.label).then(
                  setContactTypeOptions
                );
              }}
              options={contactTypeOptions || []}
              name="contactType"
              value={formValues.contactType}
              multiline={true}
              required
              validated={!!error.contactType}
              helperText={error.contactType}
              readOnly={isDisabled}
            />
          </Box>
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Contact Mode</FormLabel>
          </Grid>
          <TextInput
            type="autocomplete"
            name="contactMode"
            value={formValues.contactMode || ''}
            onChange={(e) => {
              const label = e ? e.label || e : '';
              handleChange({ target: { name: 'contactMode', value: label } });
              if (label) {
                saveClinicalDefault('contactModes', label).then(
                  setContactModeOptions
                );
              }
            }}
            options={contactModeOptions || []}
            onDelete={(option) => {
              deleteClinicalDefault('contactModes', option.label).then(
                setContactModeOptions
              );
            }}
            readOnly={isDisabled}
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Associated Referral</FormLabel>
          </Grid>
          <AssociatedNote
            name="associatedReferral"
            handleChange={handleChange}
            value={formValues.associatedReferral}
            noteOptions={referralOptions}
            readOnly={isDisabled}
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Contact Date</FormLabel>
          </Grid>
          <DateInput
            name="contactDate"
            value={asPickerDay(formValues.contactDate)}
            onChange={(val) => {
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
              setFormValues((prev) => {
                const next = {
                  ...prev,
                  contactDate: val,
                  ageAtRecord: computeAgeAtRecord(val)
                };
                if (!prev.reminderEnabled || !reminderTouched) {
                  next.reminderDate = computeDefaultReminderDate(val);
                }
                return next;
              });
            }}
            variant="outline"
            minDate={new Date(1600, 0, 0)}
            liveText={true}
            error={!!error?.contactDate}
            helperText={error?.contactDate}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Age at Contact"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND contact date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.contactDate}
            onChange={
              patientDob && formValues.contactDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Duration (minutes)</FormLabel>
          </Grid>
          <TextInput
            type="number"
            name="contactTime"
            value={formValues.contactTime || ''}
            onChange={handleChange}
            InputProps={{ readOnly: isDisabled }}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Contact Status</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <FormControl fullWidth>
              <Select
                style={{ width: '100%' }}
                name="contactStatus"
                value={formValues.contactStatus}
                onChange={handleChange}
                readOnly={isDisabled}
              >
                {/* The configured list, with the record's own value added
                    when the clinic no longer offers it, so a status written by
                    an import or retired by an administrator can still be saved
                    (TGCLOUD-1784). Falls back to the wording this product has
                    always written until the list loads. */}
                {offeredStatusLabels(
                  contactStatusOptions,
                  formValues.contactStatus,
                  'contactStatuses'
                ).map((option) => (
                  <MenuItem value={option} key={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* 
      // REMOVE as per ticket [TGC-767]
      <Grid item xs={6}>
        <Box sx={{ display: 'flex', position: 'relative', paddingBottom: 1 }}>
          <TextInput
            label="Contact Type"
            type="autocomplete"
            name="contactType"
            value={formValues.contactType || ''}
            onChange={(value) => {
              setFormValues({
                ...formValues,
                contactType: value
              });
            }}
            options={contactTypeOptions || []}
            helperText="Please select or enter a contact type"
          />
        </Box>
      </Grid> 
      <Grid item xs={12}>
        <Grid sx={{ paddingBottom: 1 }}>
          <FormLabel>Link</FormLabel>
        </Grid>
        <Box sx={{ display: 'flex', position: 'relative' }}>
          <TextField
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="link"
            value={formValues.link}
          />
        </Box>
      </Grid> */}
        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Contact Notes</FormLabel>
          </Grid>
          <Box
            data-va-anchor="pedigree.contact.notesEditor"
            sx={{ display: 'flex', position: 'relative' }}
          >
            {catalogue ? (
              <CKEditor
                editor={ClassicEditor}
                disabled={isDisabled}
                config={{
                  exportToPDF,
                  exportToDocs,
                  mergeFieldsCatalogue: catalogue.groups,
                  plugins: [
                    ...basicPlugins,
                    ExportToPdf,
                    ExportToDocs,
                    MergeFieldDropdown,
                    InsertConditionalBlock,
                    InsertRepeatingBlock,
                    TemplateDropdown,
                    MergeFieldsPreviewDropdown
                  ],
                  toolbar: toolbar,
                  ...baseConfig,
                  mergeFieldsDefaults
                }}
                onReady={(editor) => {
                  editorRef.current = editor;
                  editor._lmResolveContext = mergeResolveContext;
                  editor._lmCatalogueIndex = mergeCatalogueIndex;
                  editor._lmResolveAmbiguity = resolveAmbiguity;
                  editor._lmGenerationMeta = {
                    enabled: !!catalogue?.v2Enabled,
                    familyId: user.familyId,
                    familyMemberId
                  };
                  // Warn (don't fail silently) if the audit record can't save.
                  editor._lmOnGenerationRecorded = (status) => {
                    if (status && status.ok === false) {
                      setSnack({
                        status: true,
                        msg: t(
                          'Letter exported, but its audit record could not be saved. Please retry.'
                        ),
                        severity: 'warning'
                      });
                    }
                  };
                  editor._lmContactContext = {
                    contactType: formValues.contactType,
                    contactMode: formValues.contactMode,
                    contactStatus: formValues.contactStatus
                  };
                  editor.setData(formValues.contactNotes);
                  setEditorReady(true);
                }}
                onChange={handleEditorChange}
              />
            ) : (
              <CircularProgress size={22} />
            )}
          </Box>
        </Grid>
        <UploadFileList
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(next) => {
            // Functional: uploads can finish faster than this form re-renders.
            setFormValues((prev) => ({ ...prev, fileAttachments: next }));
            setUnsavedChanges((prev) => ({ ...prev, edit: true }));
          }}
          readOnly={isDisabled}
          onBusyChange={setUploadingFiles}
          onUploadComplete={(uploadData, name) =>
            attachmentChanges.trackUpload(name)
          }
        />

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                disabled={isDisabled}
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
              />
            </Grid>
            <Grid>
              <Typography>
                {formValues.showInFamily ? t('Yes') : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Staff Member */}
        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextInput
                  label="Staff Member"
                  name="staffMember"
                  type="multiple-select"
                  value={formValues?.staffMember || []}
                  onChange={handleChange}
                  options={allStaffMembers || []}
                  mapper={staffMembersMap || {}}
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        {/* Request Checking*/}
        {formValues.staffMember && formValues.staffMember.length > 0 && (
          <Grid id="top" size={12}>
            <Grid sx={{ paddingBottom: 1 }}>
              <FormLabel>Assign to Staff for checking</FormLabel>
            </Grid>
            <Grid size={12}>
              <SelectInput
                fullWidth={true}
                variant="outlined"
                name="requestChecking"
                options={reqCheckingOptions}
                value={formValues.requestChecking || ''}
                onChange={handleChange}
                disabled={isDisabled}
              />
            </Grid>
          </Grid>
        )}

        {/*Date Requested Checking*/}
        {formValues.requestChecking && (
          <Grid size={12}>
            <Grid sx={{ paddingBottom: 1 }}>
              <FormLabel>Assigned Date</FormLabel>
            </Grid>
            <DateInput
              readOnly={isDisabled}
              name="assignedDate"
              value={toLocalDateFromInstant(formValues.assignedDate)}
              onChange={(e) =>
                setFormValues((prevValues) => ({
                  ...prevValues,
                  assignedDate: e
                }))
              }
              variant="outline"
            />
          </Grid>
        )}

        {/*Approved*/}
        {formValues.requestChecking && formValues.assignedDate && (
          <Grid
            container
            size={12}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 6,
                sm: 7
              }}
            >
              <FormLabel component="label" className="form-label">
                Approved
              </FormLabel>
            </Grid>
            <Grid
              container
              size={{
                xs: 6,
                sm: 3
              }}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid>
                <Switch
                  color="secondary"
                  checked={formValues.approved ?? false}
                  onChange={onToggleSwitch}
                  name="approved"
                  disabled={isDisabled}
                />
              </Grid>
              <Grid>
                <Typography>
                  {formValues.approved ? t('Yes') : t('No')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/*Date Approved*/}
        {formValues.approved && (
          <Grid size={12}>
            <Grid sx={{ paddingBottom: 1 }}>
              <FormLabel>Date Approved</FormLabel>
            </Grid>
            <DateInput
              name="dateApproved"
              value={toLocalDateFromInstant(formValues.dateApproved)}
              onChange={(e) =>
                setFormValues((prevValues) => ({
                  ...prevValues,
                  dateApproved: e
                }))
              }
              variant="outline"
              readOnly={isDisabled}
            />
          </Grid>
        )}

        {/* Reminder */}
        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <Grid>
              <Switch
                disabled={isDisabled}
                checked={!!formValues.reminderEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                  setFormValues((prev) => {
                    const next = { ...prev, reminderEnabled: enabled };
                    // if turning ON and no reminderDate yet, default to contactDate
                    if (enabled && !prev.reminderDate) {
                      next.reminderDate = computeDefaultReminderDate(
                        prev.contactDate
                      );
                    }
                    return next;
                  });
                }}
                name="reminderEnabled"
                color="secondary"
              />
            </Grid>
            {formValues.reminderEnabled && (
              <Grid
                sx={{ marginLeft: 14 }}
                size={{
                  xs: 12,
                  sm: 6
                }}
              >
                <Grid sx={{ paddingBottom: 1 }}>
                  <FormLabel>Reminder Date</FormLabel>
                </Grid>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate}
                  // Optional: keep it >= contactDate, or remove if not required
                  minDate={asPickerDay(formValues.contactDate) || new Date()}
                  onChange={(val) => {
                    setReminderTouched(true);
                    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    setFormValues((prev) => ({ ...prev, reminderDate: val }));
                  }}
                  variant="outline"
                  helperText={t('Default is the Contact Date')}
                  readOnly={isDisabled}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing && !isDisabled ? handleOpenModal : null}
          handleClose={handleClose}
          addLabel={`Save New Contact`}
          saveButtonProps={
            uploadingFiles
              ? {
                  disabled: true,
                  title: 'Waiting for files to finish uploading'
                }
              : undefined
          }
        />
      </Grid>
      <CustomDialog
        open={!!ambiguousRequest}
        title="Which record should this letter use?"
        handleClose={handleAmbiguousCancel}
        showActions
        saveBtnLabel="Use selected"
        onSave={() =>
          Promise.resolve(handleAmbiguousConfirm(ambiguousSelections))
        }
      >
        <Typography variant="body2" gutterBottom>
          {t(
            'This record has more than one entry for the fields below. Pick which one the note should use.'
          )}
        </Typography>
        {(ambiguousRequest?.ambiguous || []).map((section) => (
          <Box key={section.section} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {section.label}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={section.items.indexOf(
                  ambiguousSelections[section.section]
                )}
                onChange={(e) =>
                  setAmbiguousSelections((prev) => ({
                    ...prev,
                    [section.section]: section.items[e.target.value]
                  }))
                }
              >
                {section.items.map((item, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {ambiguousItemLabel(item, section)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
      </CustomDialog>
    </form>
  );
}
